import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, Sparkles, ArrowLeft, CheckCircle2, Building2 } from "lucide-react";
import { api } from "../lib/api";
import PublicHeader from "../components/branding/PublicHeader";

/**
 * CockpitPortfolioPage — Vitrine publique des sites réalisés avec WECOCKPIT.
 * URL : /realisations-cockpit  (publique, sans auth)
 */
export default function CockpitPortfolioPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agency, setAgency] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/agency/public-portfolio"),
      api.get("/settings"),
    ]).then(([pRes, sRes]) => {
      const data = pRes.data || {};
      setItems(data.items || []);
      setAgency(sRes.data || {});
    }).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const agencyName = agency.agency_brand_name || agency.agency_name || "Agence MSC";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/40 text-slate-900" data-testid="cockpit-portfolio-page">
      {/* Background blobs subtils */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-sky-200/50 rounded-full blur-3xl"/>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-emerald-200/50 rounded-full blur-3xl"/>
      </div>

      {/* Header partagé */}
      <PublicHeader tone="light" agencyName={agencyName}/>

      <main className="relative max-w-6xl mx-auto px-6 py-10 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow-sm border border-slate-200 text-xs font-semibold uppercase tracking-wider mb-4 text-slate-700">
            <Sparkles className="w-3 h-3 text-emerald-500"/> Portfolio · {items.length} {items.length > 1 ? "réalisations" : "réalisation"}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 bg-gradient-to-br from-slate-900 via-slate-700 to-sky-700 bg-clip-text text-transparent">
            Sites réalisés avec WECOCKPIT
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Découvrez les sites professionnels créés et pilotés par {agencyName} grâce à la plateforme WECOCKPIT.
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-sky-500"/>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
            <Building2 className="w-12 h-12 mx-auto text-slate-400 mb-4"/>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Premières réalisations bientôt</h3>
            <p className="text-sm text-slate-500">
              Nos premiers sites clients seront listés ici dès leur mise en ligne.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item, idx) => (
              <a
                key={idx}
                href={item.site_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-slate-300 hover:scale-[1.02] transition-all duration-300"
                data-testid={`portfolio-item-${idx}`}
              >
                <div
                  className="h-32 relative overflow-hidden flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${item.primary_color || "#0EA5E9"}, #0F2744)` }}
                >
                  {item.logo_url ? (
                    <img src={item.logo_url} alt={item.company_name} className="max-h-16 max-w-[60%] object-contain bg-white rounded-lg p-2"/>
                  ) : (
                    <div className="text-3xl font-black text-white opacity-70">
                      {(item.company_name || "?").charAt(0)}
                    </div>
                  )}
                  {item.validated_at && (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-500/90 backdrop-blur text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
                      <CheckCircle2 className="w-2.5 h-2.5"/> Validé
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 text-base group-hover:text-sky-700 transition truncate">
                    {item.company_name}
                  </h3>
                  {item.sector && (
                    <div className="text-xs text-slate-500 mt-0.5">{item.sector}</div>
                  )}
                  {item.site_url && (
                    <div className="text-[11px] text-slate-500 mt-2 truncate font-mono inline-flex items-center gap-1 group-hover:text-emerald-600 transition">
                      {item.site_url.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 35)}
                      <ExternalLink className="w-3 h-3"/>
                    </div>
                  )}
                  {item.live_since && (
                    <div className="text-[10px] text-slate-400 mt-2">
                      En ligne depuis {new Date(item.live_since).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-sky-500 to-emerald-500 rounded-2xl p-8 shadow-xl">
          <h2 className="text-2xl font-bold mb-2 text-white">Vous aussi, votre site avec WECOCKPIT</h2>
          <p className="text-sm text-white/90 mb-5">
            Discutons de votre projet : site vitrine, e-commerce, prise de RDV, blog, multi-sites…
          </p>
          <Link
            to="/onboarding"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-slate-900 font-bold shadow-md hover:bg-slate-50 transition"
            data-testid="portfolio-cta-onboarding"
          >
            Demander un devis <ArrowLeft className="w-4 h-4 rotate-180"/>
          </Link>
        </div>
      </main>

      <footer className="relative max-w-6xl mx-auto px-6 py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} WECOCKPIT — Solution opérée par {agencyName}
      </footer>
    </div>
  );
}
